import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TopNavigation = ({ scrollToSection }) => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { id: 'hero', label: 'Inicio' },
    { id: 'amenities', label: 'Amenidades' },
    { id: 'location', label: 'Ubicación' },
    { id: 'pricing', label: 'Precios' },
    { id: 'testimonials', label: 'Testimonios' }
  ];

  const handleNavClick = (sectionId) => {
    scrollToSection(sectionId);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/98 backdrop-blur-xl shadow-xl border-b-2 border-gray-200'
            : 'bg-white/80 backdrop-blur-md border-b border-gray-200'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => handleNavClick('hero')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg ring-2 ring-orange-200">
                <Image
                  src="/assets/images/logo-3-1768280661456.jpg"
                  alt="Residencial 3 Volcanes Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-heading font-black text-gray-900">
                  3 Volcanes
                </h1>
                <p className="text-xs text-gray-600 font-bold">Tu inversión segura</p>
              </div>
            </motion.div>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-8">
              {menuItems?.map((item) => (
                <button
                  key={item?.id}
                  onClick={() => handleNavClick(item?.id)}
                  className="text-sm font-bold text-gray-700 hover:text-orange-600 transition-colors relative group"
                >
                  {item?.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-red-600 transition-all group-hover:w-full" />
                </button>
              ))}
            </div>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/signin')}
                className="font-bold text-gray-700 hover:text-orange-600 hover:bg-orange-50 border-2 border-transparent hover:border-orange-200"
              >
                <Icon name="LogIn" size={18} className="mr-2" />
                Iniciar Sesión
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/signup')}
                className="font-bold bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 shadow-lg hover:shadow-xl transition-all border-2 border-orange-700"
              >
                <Icon name="UserPlus" size={18} className="mr-2" />
                Registrarte
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-xl hover:bg-orange-50 transition-colors border-2 border-gray-200 hover:border-orange-300"
              aria-label="Toggle menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} className="text-gray-900" />
            </button>
          </div>
        </div>
      </motion.nav>
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          className="fixed inset-0 z-40 lg:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <motion.div
            className="absolute top-20 left-0 right-0 bg-white border-b-2 border-gray-200 shadow-2xl"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="container mx-auto px-4 py-6 space-y-4">
              {menuItems?.map((item) => (
                <button
                  key={item?.id}
                  onClick={() => handleNavClick(item?.id)}
                  className="block w-full text-left px-4 py-3 rounded-xl text-gray-900 hover:bg-orange-50 hover:text-orange-600 transition-colors font-bold border-2 border-transparent hover:border-orange-200"
                >
                  {item?.label}
                </button>
              ))}
              <div className="pt-4 border-t-2 border-gray-200 space-y-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    navigate('/signin');
                    setIsMobileMenuOpen(false);
                  }}
                  className="font-bold border-2 border-gray-300 hover:border-orange-500 hover:bg-orange-50 hover:text-orange-600"
                >
                  <Icon name="LogIn" size={18} className="mr-2" />
                  Iniciar Sesión
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  onClick={() => {
                    navigate('/signup');
                    setIsMobileMenuOpen(false);
                  }}
                  className="font-bold bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 shadow-lg border-2 border-orange-700"
                >
                  <Icon name="UserPlus" size={18} className="mr-2" />
                  Registrarte
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default TopNavigation;